import { PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/20/solid';
import {
  BoltIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';

import {
  SHARE_RECIPIENT_LIMIT,
  isPaymentRequiredError,
} from '@personal-trainer-app/shared';
import {
  planService,
  workoutService,
} from '@personal-trainer-app/shared/src/services';
import {
  buildShareConfig,
  formatHandle,
  getShareConfirmation,
  getSharedMessage,
  SHARE_NOTE_MAX_LENGTH,
} from '@personal-trainer-app/shared/src/utils/share';

import type { ShareModalProps } from './Share.types';
import type { SelectizeOption } from '@/types/forms';

import { FormSelectize, FormTextarea } from '@/components/forms';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { CardTag } from '@/components/ui/CardTag';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { useSelectizeLoader } from '@/hooks';

/** Cap on recipients per share — keeps the fan-out bounded. */
// The API's cap, not a number of this component's own — it capped at 20, which
// let someone pick eleven recipients and meet a validation error on submit.
const MAX_RECIPIENTS = SHARE_RECIPIENT_LIMIT;
/**
 * ShareModal - quick-share flow for a Workout or Plan.
 *
 * A `Modal`, which becomes a bottom sheet below `md` on its own.
 * Collects friend recipients + an optional note, then shares the resource with
 * each recipient (one `/share` call per recipient — the API takes one at a time).
 * Plans additionally surface a "Schedule this plan" branch that routes out to the
 * full customise flow.
 *
 * Controlled by `ShareProvider`; rendered once at the app shell, opened via
 * `useShare().openShare(target)`.
 */
export function ShareModal({
  target,
  onClose,
  onSchedulePlan,
}: ShareModalProps): React.JSX.Element | null {
  const toast = useToast();

  const [recipients, setRecipients] = useState<SelectizeOption[]>([]);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Recipient suggestions come from the backend quick-search endpoint
  // (GET /api/selectize/friend) — the same loader the challenge form uses.
  // value = username (the share recipient key), label = display name.
  const loadFriends = useSelectizeLoader('friend');

  // Retain the last target so the body stays populated through the close animation.
  const [snapshot, setSnapshot] = useState(target);
  useEffect(() => {
    if (target) setSnapshot(target);
  }, [target]);

  // Reset the form each time a (new) resource is opened.
  useEffect(() => {
    if (target) {
      setRecipients([]);
      setNote('');
      setIsSubmitting(false);
    }
  }, [target]);

  const isOpen = target !== null;
  const resource = target ?? snapshot;

  const handleShare = useCallback(async () => {
    if (!resource || recipients.length === 0 || isSubmitting) return;

    const toSend = recipients;
    // The /share endpoint takes the full recipient list in one request.
    const config = buildShareConfig(resource, toSend, note);
    setIsSubmitting(true);

    try {
      if (resource.type === 'plan') {
        await planService.sharePlan(config);
      } else {
        await workoutService.shareWorkout(config);
      }
      toast.success(
        getSharedMessage(toSend.map((recipient) => recipient.label))
      );
      onClose();
    } catch (error) {
      // A 402 is the app-level paywall presenter's to show — its modal opens
      // over this one automatically, and a red toast underneath would present
      // the refusal twice. The passive guard is the whole fix here: this
      // modal mounts under ShareProvider, OUTSIDE PaywallProvider, so
      // `usePaywall()` is not reachable. The draft stays intact behind the
      // paywall.
      if (!isPaymentRequiredError(error)) {
        toast.error(
          error instanceof Error
            ? error.message
            : 'Couldn’t share this. Please try again.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [resource, recipients, note, isSubmitting, toast, onClose]);

  // Lock every dismissal path while a share is in flight (mirrors the disabled
  // Cancel button) so the modal can't be backed out from under an active submit.
  const handleRequestClose = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  if (!resource) return null;

  const isPlan = resource.type === 'plan';
  const confirmation = getShareConfirmation(
    recipients.map((recipient) => recipient.label),
    resource.type
  );
  const HeroIcon = isPlan ? ClipboardDocumentListIcon : BoltIcon;

  const body = (
    <div className="space-y-5">
      {/* Resource hero — confirms what's being shared */}
      <div className="flex items-center gap-3 rounded-xl border border-stroke-subtle bg-contrast-700 p-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sakura-wash">
          <HeroIcon className="h-6 w-6 text-primary-300" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-lg font-semibold text-neutral-50">
            {resource.name}
          </p>
          <p className="truncate text-body-sm text-neutral-500">
            {resource.meta}
          </p>
        </div>
        <CardTag variant="outline" caps className="shrink-0">
          {resource.type}
        </CardTag>
      </div>

      {/* Recipients */}
      <FormSelectize
        label="Share with"
        multiple
        minSearchLength={0}
        maxSelections={MAX_RECIPIENTS}
        placeholder="Search friends…"
        loadOptions={loadFriends}
        value={recipients}
        onChange={(value) =>
          setRecipients(Array.isArray(value) ? value : value ? [value] : [])
        }
        emptyMessage="No friends found"
        renderOption={(option) => (
          <div className="flex items-center gap-2">
            <Avatar name={option.label} size={28} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-neutral-50">{option.label}</div>
              <div className="truncate text-body-sm text-neutral-500">
                {formatHandle(option.value)}
              </div>
            </div>
          </div>
        )}
        renderTag={(option, onRemove) => (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-stroke-accent bg-sakura-wash py-0.5 pl-0.5 pr-1.5 text-body-md text-primary-300 transition-colors hover:bg-sakura-tint">
            <Avatar name={option.label} size={20} />
            <span className="max-w-[140px] truncate">{option.label}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="rounded-sm text-primary-300/80 transition-colors hover:bg-sakura-shade hover:text-primary-300 focus:outline-none focus-visible:ring-state-focus"
              aria-label={`Remove ${option.label}`}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      />

      {/* Optional note */}
      <FormTextarea
        label="Add a note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Say something… (optional)"
        rows={2}
        resize="none"
        maxLength={SHARE_NOTE_MAX_LENGTH}
      />

      {/* Plan-only: branch to the full schedule/customise flow */}
      {isPlan && onSchedulePlan && (
        <button
          type="button"
          onClick={() => onSchedulePlan(resource, { recipients, note })}
          className="group flex w-full items-center gap-3 rounded-xl border border-stroke-default bg-contrast-700 p-3 text-left transition-colors hover:border-stroke-strong focus:outline-none focus-visible:ring-state-focus"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sakura-wash">
            <CalendarDaysIcon
              className="h-5 w-5 text-primary-300"
              aria-hidden="true"
            />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-body-md text-neutral-50">
              Schedule this plan
            </span>
            <span className="block text-body-sm text-neutral-500">
              Assign each workout a date for them
            </span>
          </span>
          <ChevronRightIcon
            className="h-5 w-5 shrink-0 text-neutral-500 transition-colors group-hover:text-neutral-300"
            aria-hidden="true"
          />
        </button>
      )}

      {/* Live confirmation line */}
      {confirmation && (
        <p
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="text-body-md text-neutral-400"
        >
          <PaperAirplaneIcon
            className="mr-1.5 inline h-4 w-4 -translate-y-px text-neutral-600"
            aria-hidden="true"
          />
          <span className="text-neutral-50">{confirmation.who}</span>{' '}
          {confirmation.rest}
        </p>
      )}
    </div>
  );

  const footer = (
    <div className="flex w-full items-center justify-between gap-3">
      <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleShare}
        loading={isSubmitting}
        disabled={recipients.length === 0}
        leftIcon={<PaperAirplaneIcon className="h-5 w-5" />}
      >
        Share
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleRequestClose}
      size="sm"
      title="Share"
      closeOnBackdropClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      footer={footer}
    >
      {body}
    </Modal>
  );
}

export default ShareModal;
